import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { openWhatsApp, buildWhatsAppUrl } from "@/lib/whatsapp";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Sparkles,
  Car,
  Megaphone,
  Crown,
  Zap,
  ShieldCheck,
  Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BENITO_MODULES = [
  {
    n: "01",
    title: "Entrare nel settore senza bruciarti",
    desc:
      "Come si ragiona da operatore e non da improvvisato. Gli errori che ti svuotano il conto prima ancora del primo cliente.",
  },
  {
    n: "02",
    title: "Dove trovare le auto al prezzo giusto",
    desc:
      "Piattaforme reali in Italia ed estero, contatti diretti, come distinguere un'opportunità da un bidone.",
  },
  {
    n: "03",
    title: "Riconoscere un'auto che si vende",
    desc:
      "Leggere il mercato, non l'entusiasmo. Configurazioni che girano, configurazioni che ti rimangono in piazzale.",
  },
  {
    n: "04",
    title: "Fare prezzo senza regalare margine",
    desc:
      "Mercato, percezione, velocità di uscita, marginalità. Non è prezzare: è non perdere soldi quando vendi.",
  },
  {
    n: "05",
    title: "La trattativa che vende 50 auto al mese",
    desc:
      "Come Benito firma 50 vetture al mese: gestione del cliente, lettura delle obiezioni, chiusura.",
  },
  {
    n: "06",
    title: "Perché una concessionaria non vende",
    desc:
      "Stock, struttura, presenza fisica: cosa rompe la conversione e come si ripara, errore per errore.",
  },
  {
    n: "07",
    title: "Garanzie auto + contatti diretti",
    desc:
      "Come ti interfacci con le compagnie di garanzia. Contatti operativi forniti direttamente da Benito.",
  },
];

const MIRIANA_MODULES = [
  {
    n: "08",
    title: "Smettere di pubblicare a caso",
    desc:
      "Posizionarti come leader del tuo mercato. Le leve di neuromarketing che hanno generato +23M€ extra ai clienti Mirlo's.",
  },
  {
    n: "09",
    title: "Contenuti che portano in trattativa",
    desc:
      "Da zero: scrittura, costruzione, tono. Non visualizzazioni vuote — persone che ti scrivono per comprare.",
  },
  {
    n: "10",
    title: "Social = sistema commerciale",
    desc:
      "Smetti di usarli come bacheca. Struttura, copy con psicologia del consumatore, continuità, obiettivo.",
  },
  {
    n: "11",
    title: "Autonomia totale dal marketing esterno",
    desc:
      "Mai più agenzie e SMM che ti svuotano il conto ogni mese senza portarti niente. Lo gestisci tu, con testa imprenditoriale.",
  },
];

const FAQ = [
  {
    q: "A chi è rivolto il percorso?",
    a: "A chi vuole aprire una concessionaria da zero e a chi è già nel settore ma NON riesce a vendere abbastanza auto. Se ti riconosci, sei nel posto giusto.",
  },
  {
    q: "Chi mi seguirà?",
    a: "Il percorso è strutturato come lezioni private con Miriana e Benito. Niente aula da 200 persone, niente video preregistrati lasciati in una piattaforma e abbandonati. Lavori direttamente con loro.",
  },
  {
    q: "È detraibile?",
    a: "Sì. L'investimento è 100% detraibile dalle tasse.",
  },
  {
    q: "Quanti posti sono disponibili?",
    a: "Solo 8 in totale. È un percorso 1:1 — non possiamo seguire più persone di così senza abbassare la qualità. I posti vengono assegnati in ordine di candidatura.",
  },
  {
    q: "Cosa succede dopo che invio la candidatura?",
    a: "Cliccando il bottone si apre WhatsApp con un messaggio pronto. Il team ti risponde direttamente, ti spiega il percorso nel dettaglio e, se c'è fit, blocchi il posto.",
  },
  {
    q: "E se non sono ancora nel settore automotive?",
    a: "Perfetto: il blocco di Benito parte proprio da chi vuole entrare da zero. Saprai cosa comprare, come trattare, come fare prezzo e come vendere — prima di rischiare un solo euro sbagliato.",
  },
];

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_tir-mirlos-academy/artifacts/uia11ftb_IMG_2397.JPG";

const BENITO_IMAGE =
  "https://customer-assets.emergentagent.com/job_tir-mirlos-academy/artifacts/rg5u0mpa_IMG_4679.jpg";

function SeatsBadge({ remaining, total, compact = false }) {
  return (
    <div
      data-testid="seats-badge"
      className={`inline-flex items-center gap-3 border hairline ${
        compact ? "px-3 py-1.5" : "px-4 py-2"
      } rounded-full bg-white/[0.03] backdrop-blur`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full pulse-dot bg-[#6000FF]" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#6000FF]" />
      </span>
      <span className="mono text-[11px] tracking-[0.25em] uppercase text-white/80">
        Posti rimasti
      </span>
      <span
        data-testid="seats-remaining"
        className="mono text-sm font-bold text-white"
      >
        {remaining}
        <span className="text-white/40">/{total}</span>
      </span>
    </div>
  );
}

function PrimaryCTA({ label = "Candidati e blocca il posto", testid = "cta-primary" }) {
  return (
    <button
      data-testid={testid}
      onClick={() => openWhatsApp()}
      className="group btn-purple inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm md:text-base font-semibold uppercase tracking-[0.15em]"
    >
      <span>{label}</span>
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/30 group-hover:translate-x-1 transition-transform">
        <ArrowUpRight className="h-4 w-4" />
      </span>
    </button>
  );
}

function WhatsAppIcon({ className = "h-4 w-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.014 21.75c-1.85 0-3.63-.489-5.192-1.413l-.372-.223-3.858 1.014 1.032-3.767-.243-.386c-1.017-1.62-1.55-3.478-1.55-5.4 0-5.598 4.559-10.156 10.157-10.156 2.714 0 5.264 1.058 7.178 2.977 1.916 1.921 2.973 4.469 2.973 7.181-.001 5.598-4.56 10.155-10.156 10.155zM20.52 3.449C18.257 1.19 15.24-.05 12.014-.05c-6.634 0-12.033 5.399-12.033 12.033 0 2.121.554 4.194 1.607 6.014L-.13 24l6.15-1.615c1.755.957 3.729 1.461 5.744 1.461h.005c6.633 0 12.033-5.399 12.033-12.033 0-3.223-1.238-6.244-3.498-8.514z" />
    </svg>
  );
}

function InlineCTABand({ testid, kicker, headline, onApply, remaining, total }) {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="rounded-3xl border border-[#6000FF]/40 bg-gradient-to-r from-[#0c0712] via-[#0a0a0a] to-[#0c0712] p-7 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#9D4CDD] font-semibold">
            {kicker}
          </div>
          <p className="mt-2 text-xl md:text-2xl font-extrabold tracking-tight leading-snug text-white">
            {headline}
          </p>
        </div>
        <div className="flex items-center gap-5 shrink-0">
          <div className="hidden sm:block">
            <SeatsBadge remaining={remaining} total={total} compact />
          </div>
          <button
            data-testid={testid}
            onClick={onApply}
            className="group btn-purple btn-glow inline-flex items-center gap-3 rounded-full px-6 py-3.5 text-xs md:text-sm font-bold uppercase tracking-[0.18em] whitespace-nowrap"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Parla con noi
          </button>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [seats, setSeats] = useState({ total: 8, taken: 6, remaining: 2 });
  const [displayedRemaining, setDisplayedRemaining] = useState(null);
  const animatedOnceRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const fetchSeats = async () => {
      try {
        const r = await axios.get(`${API}/seats`);
        if (alive) setSeats(r.data);
      } catch (e) {
        // ignore silently — fallback to defaults
      }
    };
    fetchSeats();
    const t = setInterval(fetchSeats, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Scarcity counter disabled — seats already at floor (2/8).
  useEffect(() => {
    if (animatedOnceRef.current) return;
    if (seats.remaining == null) return;
    animatedOnceRef.current = true;
    setDisplayedRemaining(seats.remaining);
  }, [seats.remaining]);

  // Effective value shown: admin updates lower win immediately; otherwise animated wins
  const effectiveRemaining =
    displayedRemaining != null
      ? Math.min(displayedRemaining, seats.remaining)
      : seats.remaining;

  const handleApply = async () => {
    // log application then redirect
    try {
      await axios.post(`${API}/applications`, {
        name: "Candidatura WhatsApp",
        note: "Click su CTA principale, redirect a WhatsApp",
      });
    } catch (_) {
      // non-blocking
    }
    toast.success("Ti reindirizziamo su WhatsApp…");
    openWhatsApp();
  };

  return (
    <div className="bg-[#050505] text-white min-h-screen overflow-x-hidden">
      {/* ============== NAV ============== */}
      <nav
        data-testid="top-nav"
        className="sticky top-0 z-40 backdrop-blur-md bg-[#050505]/70 border-b hairline"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 rounded-md bg-[#6000FF] grid place-items-center">
              <span className="text-[10px] font-black">TM</span>
            </div>
            <div className="leading-none">
              <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/60">
                Accademia
              </div>
              <div className="text-sm font-bold tracking-tight">
                TIRCAR <span className="text-white/40">x</span> MIRLO'S
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-xs mono tracking-[0.25em] uppercase text-white/70">
            <a href="#programma" className="hover:text-white">Programma</a>
            <a href="#mentori" className="hover:text-white">Mentori</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <SeatsBadge
              compact
              remaining={effectiveRemaining}
              total={seats.total}
            />
          </div>
        </div>
      </nav>

      {/* ============== HERO ============== */}
      <section className="relative grain">
        {/* purple radial glow */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 0%, rgba(96,0,255,0.25), transparent 70%), radial-gradient(40% 40% at 90% 30%, rgba(96,0,255,0.18), transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT: copy */}
            <div className="lg:col-span-8 fade-up">
              <h1
                data-testid="hero-headline"
                className="text-balance text-[38px] leading-[1] sm:text-5xl md:text-6xl lg:text-[64px] font-extrabold tracking-tight uppercase"
              >
                Vendi il{" "}
                <span className="inline-block bg-[#6000FF] text-white px-3 -mx-1">
                  doppio
                </span>{" "}
                delle tue auto in <span className="text-white">90 giorni.</span>
              </h1>

              <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 font-light leading-relaxed">
                Utilizza da subito il{" "}
                <span className="text-white font-semibold uppercase tracking-wider">
                  metodo TirCar
                </span>
                : compra dai migliori fornitori al{" "}
                <span className="text-white font-medium">miglior prezzo</span>,
                ricevi{" "}
                <span className="text-white font-medium">
                  centinaia di richieste al mese
                </span>{" "}
                e vendi il <span className="text-white font-medium">doppio</span>{" "}
                delle tue auto.
              </p>
            </div>

            {/* RIGHT: image (compact portrait card) */}
            <div className="lg:col-span-4 relative fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative max-w-sm lg:max-w-none mx-auto">
                <div className="absolute -top-3 -left-3 right-6 bottom-6 border hairline rounded-2xl" />
                <div className="absolute -bottom-3 -right-3 left-6 top-6 border border-[#6000FF]/40 rounded-2xl" />
                <div className="relative overflow-hidden rounded-2xl border hairline">
                  <img
                    data-testid="hero-image"
                    src={HERO_IMAGE}
                    alt="Miriana e Benito"
                    className="w-full h-[340px] md:h-[400px] object-cover object-center grayscale-[15%]"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.9) 100%)",
                    }}
                  />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-semibold">
                        I mentori
                      </div>
                      <div className="text-lg md:text-xl font-extrabold tracking-tight">
                        Miriana &{" "}
                        <span className="text-[#9D4CDD]">Benito</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full-width centered CTA under both columns */}
          <div className="mt-14 md:mt-16 flex flex-col items-center gap-3">
            <button
              onClick={handleApply}
              data-testid="hero-cta"
              className="group btn-purple btn-glow inline-flex items-center justify-center gap-3 rounded-full px-10 py-4 text-sm md:text-base font-semibold uppercase tracking-[0.15em]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Chiedi info
            </button>
            <span
              className="text-[10px] whitespace-nowrap tracking-[0.25em] uppercase text-white/50 font-medium"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Pagamento a rate · 100% detraibile
            </span>
          </div>
        </div>

        {/* Marquee removed */}
      </section>

      {/* ============== EMPATHY / DOLORI ============== */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-4 md:pt-6 pb-8">
        <div className="max-w-3xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1]">
            Basta acqua alla gola{" "}
            <span className="text-[#9D4CDD]">a fine mese.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/80 font-medium uppercase tracking-wide">
            Non è solo fatturare di più, ma…
          </p>
          <p className="mt-4 text-lg md:text-xl text-white/75 font-light leading-relaxed">
            È arrivare al punto in cui non devi più{" "}
            <span className="text-white font-medium">
              rincorrere ogni mese clienti, incassi e scadenze
            </span>
            .
          </p>
          <p className="mt-3 text-lg md:text-xl text-white/75 font-light leading-relaxed">
            È sentirti finalmente il proprietario di un'attività che{" "}
            <span className="text-white font-semibold uppercase tracking-wider">
              funziona davvero
            </span>
            , cresce e produce incassi con continuità.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {/* ADESSO */}
          <div className="rounded-3xl border hairline bg-white/[0.025] p-8 md:p-10">
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">
              Adesso
            </div>
            <h3 className="mt-4 text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
              Ti riconosci in questo?
            </h3>
            <ul className="mt-7 space-y-4">
              {[
                {
                  key: "ferme",
                  t: (
                    <>
                      Guardi le auto{" "}
                      <span className="text-white font-semibold">ferme</span>{" "}
                      in piazzale e senti che qualcosa non va.
                    </>
                  ),
                },
                { key: "conti", t: "A fine mese i numeri non tornano — e non capisci mai davvero perché." },
                { key: "colleghi", t: "Guardi i colleghi che vendono molto più di te e ti chiedi cosa hanno di diverso.", bold: true },
                { key: "spii", t: "Spii la pagina del tuo collega, quando ad avere quella fama e a fare quelle vendite potresti esserci TU." },
                { key: "ads", t: "Sei schiavo delle ADS e spendi mila euro ogni mese senza vedere ritorni reali." },
                { key: "esperti", t: "Hai provato agenzie, SMM, \"esperti\". Hai speso. Non hai visto risultati." },
                { key: "sera", t: "La sera torni a casa stanco. Vendi, ma non riesci a fare il salto di qualità (e di incassi)." },
              ].map(({ key, t, bold }) => (
                <li key={key} className="flex items-start gap-3 text-[15px] md:text-base">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-white/25 shrink-0" />
                  <span
                    className={
                      bold
                        ? "text-white font-semibold leading-relaxed"
                        : "text-white/65 leading-relaxed"
                    }
                  >
                    {t}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* DOPO */}
          <div className="rounded-3xl border border-[#6000FF]/50 bg-gradient-to-br from-[#0c0712] to-[#050505] p-8 md:p-10 shadow-[0_30px_80px_-20px_rgba(96,0,255,0.35)]">
            <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight text-white uppercase">
              Dopo il metodo <span className="text-[#9D4CDD]">TirCar</span>
            </h3>
            <ul className="mt-7 space-y-4">
              {[
                "Sai esattamente cosa comprare, a che prezzo — e sai che si venderà.",
                "Ogni trattativa la gestisci con uno schema. Non è più questione di fortuna.",
                "I clienti ti scrivono già decisi a comprare, prima di entrare in salone.",
                "Riceverai centinaia di richieste dai social. Nessuna agenzia da pagare mai più.",
                "Le vendite crescono ogni mese e il tuo margine aumenta.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] md:text-base">
                  <span className="mt-1 h-5 w-5 rounded-full bg-[#6000FF]/20 grid place-items-center shrink-0">
                    <Check className="h-3 w-3 text-[#9D4CDD]" strokeWidth={3} />
                  </span>
                  <span className="text-white/90 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============== PAIN / DEMOLIZIONE DELLE AGENZIE (moved above Programma) ============== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-8 md:pb-12">
        {/* Empty wallet visual — sits above the section */}
        <div className="mb-14 md:mb-20 max-w-3xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border hairline aspect-[16/7]">
            <img
              src="/empty-wallet.jpg"
              alt="Mani che aprono un portafogli vuoto"
              className="w-full h-full object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(60% 70% at 50% 50%, rgba(5,5,5,0) 30%, rgba(5,5,5,0.55) 100%)",
              }}
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/70 font-semibold bg-black/50 backdrop-blur px-3 py-1.5 rounded-full">
                Basta fare i conti a fine mese
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
              La verità che nessuno ti dice
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05] uppercase">
              Le agenzie ti{" "}
              <span className="text-[#6000FF]">svuotano il conto.</span>
            </h2>
            <p className="mt-6 text-white/70 font-light leading-relaxed max-w-md">
              Paghi l'agenzia per pubblicarti due reel, te la chiamano
              "strategia" e ti mandano la fattura ogni mese. Risultato? Stesse
              auto in piazzale, stesso fatturato, problemi del settore che
              restano.
            </p>
            <p className="mt-4 text-white/70 font-light leading-relaxed max-w-md">
              Con noi imparerai a svolgere con eccellenza: marketing, gestione
              trattativa e miglioramento dei tuoi acquisti.{" "}
              <span className="text-white font-semibold uppercase tracking-wider">
                Nozioni tue per sempre.
              </span>
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                k: "Prima",
                items: [
                  "Pubblichi le auto come tutti gli altri",
                  "Paghi ogni mese senza vedere clienti",
                  "Dipendi dal passaggio fisico in salone",
                  "Tratti il prezzo per chiudere a tutti i costi",
                ],
                tone: "muted",
              },
              {
                k: "Dopo",
                items: [
                  "Costruisci autorevolezza prima della trattativa",
                  "I clienti ti scrivono già pronti a comprare",
                  "Il tuo marketing lo gestisci tu, con testa",
                  "Vendi più velocemente, senza svendere",
                ],
                tone: "purple",
              },
            ].map((b) => (
              <div
                key={b.k}
                className={`rounded-2xl border p-7 ${
                  b.tone === "purple"
                    ? "border-[#6000FF]/50 bg-[#6000FF]/[0.06]"
                    : "hairline bg-white/[0.025]"
                }`}
              >
                <div
                  className={`text-[11px] mono tracking-[0.3em] uppercase ${
                    b.tone === "purple" ? "text-[#9D4CDD]" : "text-white/40"
                  }`}
                >
                  {b.k}
                </div>
                <ul className="mt-5 space-y-3 text-[15px]">
                  {b.items.map((it) => (
                    <li key={it} className="flex items-start gap-3">
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                          b.tone === "purple"
                            ? "bg-[#6000FF]"
                            : "bg-white/30"
                        }`}
                      />
                      <span
                        className={
                          b.tone === "purple"
                            ? "text-white"
                            : "text-white/60 line-through decoration-white/20"
                        }
                      >
                        {it}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== VISUAL BAND · Stelvio + money (moved above Programma) ============== */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6 md:pt-8 pb-14 md:pb-20">
        <div className="relative rounded-3xl overflow-hidden border border-[#6000FF]/40 aspect-[16/7] md:aspect-[21/8] shadow-[0_40px_100px_-20px_rgba(96,0,255,0.45)]">
          <img
            src="/money-showroom.jpg"
            alt="Autosalone di lusso con Alfa Romeo Stelvio e banconote che piovono dall'alto"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute bottom-4 right-4 hidden md:block">
            <SeatsBadge
              compact
              remaining={effectiveRemaining}
              total={seats.total}
            />
          </div>
        </div>
      </section>

      {/* ============== PROGRAMMA / 2 BLOCCHI SINTETICI ============== */}
      <section id="programma" className="max-w-7xl mx-auto px-6 md:px-12 pt-4 md:pt-8 pb-24 md:pb-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1]">
              Cosa trovi<br />
              <span className="text-[#9D4CDD]">nel programma.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/70 font-light leading-relaxed">
            Tutto applicabile da subito alla tua concessionaria e… per sempre.
            È un investimento che{" "}
            <span className="text-white font-semibold uppercase tracking-wide">
              ti cambia la carriera.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {/* BLOCCO BENITO */}
          <article
            data-testid="block-benito"
            className="relative rounded-3xl border border-[#6000FF]/40 bg-gradient-to-br from-[#0c0712] via-[#0a0a0a] to-[#050505] p-8 md:p-10 hover:border-[#6000FF]/70 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#6000FF] grid place-items-center">
                <Car className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
                  Blocco Benito
                </div>
                <div className="text-sm font-semibold text-white/60">
                  Il Re dell'usato · 50 auto al mese
                </div>
              </div>
            </div>

            <h3 className="mt-8 text-3xl md:text-4xl font-extrabold tracking-tight uppercase leading-[1.05]">
              Vendi più auto
              <br />
              in <span className="text-[#9D4CDD]">90 giorni:</span>
            </h3>

            <p className="mt-5 text-white/75 font-light leading-relaxed">
              Il metodo operativo di Benito per comprare bene, prezzare giusto e
              chiudere la vendita al 99%.
            </p>

            <ul className="mt-8 space-y-3.5">
              {[
                "Dove trovare auto a buon prezzo, in Italia e all'estero con i contatti diretti di Benito",
                "Aumenta i tuoi margini del 33%",
                "Riconoscere subito un'auto che si vende da una che resta in piazzale",
                "Fare prezzo senza svendere il margine e senza bloccare la vendita",
                "Usa lo stesso schema di trattativa che fa vendere 50 vetture al mese a Benito",
                "Contatti diretti degli enti di garanzia auto, fornitori convenienti, finanziarie",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-1 h-5 w-5 rounded-full bg-[#6000FF]/20 grid place-items-center shrink-0">
                    <Check className="h-3 w-3 text-[#9D4CDD]" />
                  </span>
                  <span className="text-white/85">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t hairline">
              <p className="text-sm text-white/60 italic">
                "Se hai una concessionaria che vende poco, o vuoi aprirla da
                zero: questo blocco vale da solo molto più del percorso."
              </p>
            </div>
          </article>

          {/* BLOCCO MIRIANA */}
          <article
            data-testid="block-miriana"
            className="relative rounded-3xl border hairline bg-white/[0.03] p-8 md:p-10 hover:border-white/25 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white text-black grid place-items-center">
                <Megaphone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/70">
                  Blocco Miriana
                </div>
                <div className="text-sm font-semibold text-white/60">
                  CEO Mirlo's Company LLC · 23MLN di € generati ai clienti
                </div>
              </div>
            </div>

            <h3 className="mt-8 text-3xl md:text-4xl font-extrabold tracking-tight uppercase leading-[1.05]">
              Ricevi +100 richieste
              <br />
              <span className="text-[#9D4CDD]">al mese:</span>
            </h3>

            <p className="mt-5 text-white/75 font-light leading-relaxed">
              Il sistema di Miriana per costruire autorevolezza online e portare
              clienti già pronti a comprare in concessionaria.
            </p>

            <ul className="mt-8 space-y-3.5">
              {[
                "Smetti di pubblicare auto come tutti — posizionati da leader del tuo mercato",
                "Contenuti che portano persone in trattativa, non visualizzazioni vuote",
                "Tecniche di comunicazione per convincere il tuo cliente tipo",
                "Gestisci il marketing della tua concessionaria da solo, senza più agenzie",
                "Trasforma i social da bacheca passiva a sistema commerciale che vende",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px]">
                  <span className="mt-1 h-5 w-5 rounded-full bg-white/10 grid place-items-center shrink-0">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  <span className="text-white/85">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t hairline">
              <p className="text-sm text-white/60 italic">
                "Smetti di pagare ogni mese chi non capisce il tuo settore.
                Impara a farlo tu, con testa imprenditoriale."
              </p>
            </div>
          </article>
        </div>

        {/* ============== FA PER TE SE (moved from hero) ============== */}
        <div className="mt-16 md:mt-20 max-w-3xl mx-auto rounded-3xl border border-[#6000FF]/30 bg-[#6000FF]/[0.05] p-7 md:p-10">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#9D4CDD] font-bold">
            Fa per te se…
          </div>
          <ul className="mt-6 space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-1 h-5 w-5 rounded-full bg-[#6000FF]/20 grid place-items-center shrink-0">
                <Check className="h-3 w-3 text-[#9D4CDD]" strokeWidth={3} />
              </span>
              <span className="text-white/90 text-[15px] md:text-base font-light leading-relaxed">
                Hai una concessionaria che{" "}
                <span className="text-white font-medium">vende poco</span> o{" "}
                <span className="text-white font-medium">margina male</span> e{" "}
                <span className="text-white font-semibold uppercase tracking-wider">
                  vuoi rimediare subito.
                </span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 h-5 w-5 rounded-full bg-[#6000FF]/20 grid place-items-center shrink-0">
                <Check className="h-3 w-3 text-[#9D4CDD]" strokeWidth={3} />
              </span>
              <span className="text-white/90 text-[15px] md:text-base font-light leading-relaxed">
                Vuoi{" "}
                <span className="text-white font-medium">
                  aprirne una da zero
                </span>{" "}
                senza sbagliare nulla e senza perdere soldi.
              </span>
            </li>
          </ul>
          <div className="mt-6 pt-5 border-t border-[#6000FF]/20 text-xs md:text-sm text-white/60 leading-relaxed">
            <span className="text-[#9D4CDD] font-semibold uppercase tracking-wider">
              Nota ·
            </span>{" "}
            <span className="text-white font-semibold uppercase tracking-wider">
              Se vuoi aprire una concessionaria da zero,
            </span>{" "}
            serve un budget di{" "}
            <span className="text-white font-semibold">40.000€ minimi</span>{" "}
            per avviare l'attività ricoprendo i costi amministrativi, comprare
            le prime auto e formarti con noi.
          </div>
        </div>
      </section>

      {/* ============== MENTORI ============== */}
      <section id="mentori" className="bg-[#0a0a0a] border-y hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 relative">
              <div className="relative max-w-sm mx-auto lg:max-w-none">
                <div className="overflow-hidden rounded-3xl border hairline aspect-[4/5] bg-[#050505]">
                  <img
                    src={BENITO_IMAGE}
                    alt="Benito in concessionaria TirCar"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 hidden md:block bg-[#6000FF] text-white px-5 py-3 rounded-2xl shadow-2xl">
                  <div className="text-[10px] mono tracking-[0.3em] uppercase opacity-80">
                    Mentori 1:1
                  </div>
                  <div className="text-xl font-black tracking-tight">
                    Miriana · Benito
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
                Chi ti forma
              </div>
              <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
                Chi ti seguirà durante il percorso.
              </h2>
              <div className="mt-10 space-y-10">
                <div className="border-l-2 border-[#6000FF] pl-5">
                  <div className="text-[11px] tracking-[0.3em] uppercase text-[#9D4CDD] font-semibold flex items-center gap-2">
                    <Crown className="h-3.5 w-3.5" />
                    Benito · Il Re dell'usato
                  </div>
                  <p className="mt-3 text-white/85 font-light leading-relaxed">
                    Benito partiva da{" "}
                    <span className="text-white font-medium">zero</span>. Nel{" "}
                    <span className="text-white font-medium">2015</span> apre
                    TirCar inseguendo un sogno. Negli ultimi{" "}
                    <span className="text-white font-medium">3 anni</span> è
                    passato da{" "}
                    <span className="text-white font-medium">
                      15 auto vendute al mese
                    </span>{" "}
                    ad una media di{" "}
                    <span className="text-white font-medium">50</span>.
                  </p>
                  <p className="mt-3 text-white/70 font-light leading-relaxed">
                    Non ti racconterà teoria: ti mostrerà{" "}
                    <span className="text-white font-medium">
                      come è riuscito ad ottenere tutto questo
                    </span>
                    .
                  </p>
                  <div className="mt-5 rounded-xl border border-[#6000FF]/30 bg-[#6000FF]/[0.06] p-4">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-[#9D4CDD] font-semibold">
                      Imparare da Benito ha senso se…
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-white/80">
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Hai già una concessionaria ma{" "}
                          <span className="text-white font-medium">vendi poco</span>{" "}
                          o{" "}
                          <span className="text-white font-medium">margini male</span>
                          .
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Vuoi aprirne una da zero, con un budget di partenza{" "}
                          <span className="text-white font-medium">
                            di almeno 40.000€
                          </span>
                          , e non vuoi bruciarli al primo acquisto sbagliato.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Sai di avere un{" "}
                          <span className="text-white font-medium">
                            ottimo prodotto
                          </span>{" "}
                          ma non lo vendi e vuoi capire il perché.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Vuoi imparare a fare trattative che possono
                          concludersi solo con un{" "}
                          <span className="text-white font-medium">
                            "Sì, la compro."
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="border-l-2 border-white/40 pl-5">
                  <div className="text-[11px] tracking-[0.3em] uppercase text-white/60 font-semibold">
                    Miriana · CEO Mirlo's Company LLC
                  </div>
                  <p className="mt-3 text-white/85 font-light leading-relaxed">
                    Ha iniziato a{" "}
                    <span className="text-white font-medium">21 anni</span>{" "}
                    costruendo da zero la propria carriera nel marketing.
                  </p>
                  <p className="mt-3 text-white/85 font-light leading-relaxed">
                    Ha capito cosa spinge l'utente online a comprare un
                    prodotto ed è, oggi,{" "}
                    <span className="text-white font-medium">
                      punto di riferimento
                    </span>{" "}
                    per le aziende che vogliono aumentare gli incassi.
                  </p>
                  <div className="mt-5 rounded-xl border border-[#6000FF]/30 bg-[#6000FF]/[0.06] p-4">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-[#9D4CDD] font-semibold">
                      Imparare da Miriana ha senso se…
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-white/80">
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Vuoi{" "}
                          <span className="text-white font-medium">
                            vendere online
                          </span>{" "}
                          davvero, non solo "essere presente" sui social.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Sei stanco di pagare agenzie e SMM ogni mese{" "}
                          <span className="text-white font-medium">
                            senza vedere clienti
                          </span>{" "}
                          arrivare in concessionaria.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Vuoi costruirti una{" "}
                          <span className="text-white font-medium">
                            autorevolezza
                          </span>{" "}
                          che fa percepire il tuo brand prima ancora della
                          trattativa.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#6000FF] shrink-0" />
                        <span>
                          Vuoi gestire la tua comunicazione con{" "}
                          <span className="text-white font-medium">
                            testa imprenditoriale
                          </span>
                          , senza mai più dipendere da chi non capisce il tuo
                          settore.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== INLINE CTA · post mentori ============== */}
      <InlineCTABand
        testid="cta-after-mentori"
        kicker="Stanco di guadagnare poco?"
        headline="Se sei stanco di guadagnare poco, blocca il tuo posto ORA."
        onApply={handleApply}
        remaining={effectiveRemaining}
        total={seats.total}
      />

      {/* PAIN section moved above (before Programma) */}
      {/* Stelvio visual moved above Programma */}

      {/* PRICING section removed per user request */}

      {/* ============== FAQ ============== */}
      <section id="faq" className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
          Domande frequenti
        </div>
        <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight">
          Risposte dirette, come piace a noi.
        </h2>

        <Accordion type="single" collapsible className="mt-12 w-full">
          {FAQ.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b hairline"
            >
              <AccordionTrigger className="text-left text-lg md:text-xl font-semibold tracking-tight hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/70 font-light leading-relaxed pb-6 text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ============== FINAL CTA · removed per user request ============== */}

      {/* ============== FOOTER ============== */}
      <footer className="border-t hairline bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 rounded-md bg-[#6000FF] grid place-items-center">
              <span className="text-[10px] font-black">TM</span>
            </div>
            <div className="text-sm text-white/70">
              Accademia TIRCAR x MIRLO'S · Accademia Automotive
            </div>
          </div>
          <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/40">
            © 2026 · Tutti i diritti riservati
          </div>
        </div>
      </footer>

      {/* ============== FLOATING WHATSAPP ============== */}
      <a
        data-testid="floating-whatsapp"
        href={buildWhatsAppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="float-cta fixed bottom-6 left-6 z-50 inline-flex items-center gap-3 rounded-full bg-[#25D366] hover:bg-[#1DBF5C] text-white px-5 py-3.5 text-xs font-bold uppercase tracking-[0.2em] shadow-[0_20px_50px_-10px_rgba(37,211,102,0.55)] transition-colors"
      >
        <WhatsAppIcon className="h-5 w-5" />
        Parla con noi
      </a>
    </div>
  );
}
